// Gap A landing-side restore hook (RFC-0015 Phase 1, Task 1.1).
//
// The capture half lives in context/auth.tsx: on the 401 redirect it stashes the
// current `location.hash` into the pending-auth-destination (Phase 0 primitive).
// This hook is the restore half. Mounted inside the authenticated shell
// (portal-layout.tsx, in the AuthProvider subtree), it runs ONCE per shell mount:
// as soon as the `GET /api/me` bootstrap resolves authenticated, it consumes the
// pending destination and — only when the current page is the one the hash was
// captured for — re-applies the fragment so the browser scrolls to the anchor.
//
// Security (RFC-0015 §Security):
//   - The stored `returnTo` is re-sanitized on read by `consumePendingAuthDestination`.
//   - The hash is only applied when `matchesReturnTo(current, returnTo)` holds
//     (cross-path hash-leakage defense) — a stale hash for a different page is
//     dropped silently.
//   - The fragment is applied via `scrollIntoView` on the resolved element, or a
//     plain `location.hash` assignment — NEVER `innerHTML` / `eval` / any HTML sink.

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/context/auth';
import {
  consumePendingAuthDestination,
  matchesReturnTo,
} from '@/lib/auth-destination';

export function useRestorePendingDestination(): void {
  const { isLoading, isAuthenticated } = useAuth();
  const { pathname, search } = useLocation();
  // Guards the one-shot: once we have consumed (or decided not to), never run
  // the effect body again for this shell mount.
  const doneRef = useRef(false);

  useEffect(() => {
    if (doneRef.current) return;
    // Wait until the bootstrap has resolved to an authenticated session — the
    // hash only makes sense on a real landing inside the shell.
    if (isLoading || !isAuthenticated) return;
    // Mark done BEFORE consuming so a re-render can never double-consume.
    doneRef.current = true;

    const pending = consumePendingAuthDestination();
    if (!pending) return;

    const currentPathSearch = `${pathname}${search}`;
    const matched = matchesReturnTo(currentPathSearch, pending.returnTo);
    // RFC-0015 §Logging: `matched_path` is known here (the consume site does not
    // have the current path). Emitted for the consumed-and-restored event.
    console.info('[auth] pending destination restore', {
      matched_path: matched,
      has_hash: pending.hash.length > 0,
    });

    // Cross-path hash-leakage defense: only restore the fragment when we actually
    // landed on the page it was captured for, and only when there is a hash.
    if (!matched || !pending.hash) return;

    applyHash(pending.hash);
  }, [isLoading, isAuthenticated, pathname, search]);
}

/**
 * Apply a captured URL fragment safely. Prefers scrolling the matching element
 * into view; falls back to a plain `location.hash` assignment (which the browser
 * resolves) when no element matches yet. Never touches an HTML sink, and never
 * throws into render.
 */
function applyHash(hash: string): void {
  const rawId = hash.startsWith('#') ? hash.slice(1) : hash;
  let id = rawId;
  try {
    id = decodeURIComponent(rawId);
  } catch {
    // Malformed escape sequence — fall back to the raw id.
  }
  if (!id) return;

  try {
    const el = typeof document !== 'undefined' ? document.getElementById(id) : null;
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView();
      return;
    }
    // No matching anchor (or no scroll support): set the URL fragment so the
    // browser resolves it. Pure `location.hash` assignment — not an HTML sink.
    if (typeof window !== 'undefined') {
      window.location.hash = hash;
    }
  } catch {
    // Best-effort convenience — a restore failure must never break the shell.
  }
}
